import React, { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { formatDOP, formatDate } from "@/lib/format";
import { ARAPTable, ARAPSummary, PaymentDialog } from "@/components/ARAP";
import DateRangeFilter, { defaultRange30, buildQuery } from "@/components/DateRangeFilter";
import { useConfirm } from "@/components/ConfirmDialog";
import { useAuth } from "@/contexts/AuthContext";
import { useBrand } from "@/context/BrandContext";
import {
  CoaTab, JournalTab, TrialBalanceTab, LedgerTab, IncomeStatementTab, BalanceSheetTab, PeriodCloseTab, YearEndTab,
} from "@/pages/AccountingHub";
import { AssetsTab, BankTab } from "@/pages/AmeliaHub";

function ReceivablesTab() {
  const [data, setData] = useState({ items: [], totals: { "0-30": 0, "31-60": 0, "61-90": 0, "+90": 0, total: 0, count: 0 } });
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [range, setRange] = useState(defaultRange30());
  const [includeMigration, setIncludeMigration] = useState("true");
  const [bucketsInput, setBucketsInput] = useState("30,60,90"); // Iter 45.1

  // Validador reactivo: sólo pasa buckets al backend si el input es válido
  const validBuckets = useMemo(() => {
    const raw = String(bucketsInput || "").trim();
    if (!raw) return "";
    const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
    if (!parts.length) return "";
    const nums = parts.map(Number);
    if (nums.some((n) => !Number.isInteger(n) || n < 1 || n > 90)) return null;
    const sorted = Array.from(new Set(nums)).sort((a, b) => a - b);
    return sorted.join(",");
  }, [bucketsInput]);

  const load = useCallback(async () => {
    if (validBuckets === null) return; // input inválido → no recargar
    const qs = buildQuery(range);
    const sep = qs ? "&" : "?";
    const extra = validBuckets ? `&buckets=${encodeURIComponent(validBuckets)}` : "";
    const { data } = await api.get(`/accounting/receivables${qs}${sep}include_migration=${includeMigration}${extra}`);
    setData(data);
  }, [range, includeMigration, validBuckets]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="receivables-tab">
      <DateRangeFilter value={range} onChange={setRange} testidPrefix="receivables-filter" />
      {validBuckets === null && (
        <div className="text-xs text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-2.5 py-1.5 mb-2" data-testid="arap-buckets-error">
          Cortes inválidos. Usa enteros entre 1 y 90 separados por coma, ej. <code className="font-mono">15,30,60,90</code>.
        </div>
      )}
      <ARAPSummary
        totals={data.totals}
        kind="receivable"
        includeMigration={includeMigration}
        onToggleMigration={setIncludeMigration}
        bucketsInput={bucketsInput}
        onBucketsChange={setBucketsInput}
      />
      <ARAPTable kind="receivable" items={data.items} onSelectDoc={(d) => { setSelected(d); setOpen(true); }} />
      <PaymentDialog open={open} onOpenChange={setOpen} type="receivable" document={selected} onPaid={load} />
    </div>
  );
}

function PayablesTab() {
  const [data, setData] = useState({ items: [], totals: { "0-30": 0, "31-60": 0, "61-90": 0, "+90": 0, total: 0, count: 0 } });
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [range, setRange] = useState(defaultRange30());
  const [bucketsInput, setBucketsInput] = useState("30,60,90"); // Iter 45.3

  const validBuckets = useMemo(() => {
    const raw = String(bucketsInput || "").trim();
    if (!raw) return "";
    const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
    if (!parts.length) return "";
    const nums = parts.map(Number);
    if (nums.some((n) => !Number.isInteger(n) || n < 1 || n > 365)) return null;
    return Array.from(new Set(nums)).sort((a, b) => a - b).join(",");
  }, [bucketsInput]);

  const load = useCallback(async () => {
    if (validBuckets === null) return;
    const qs = buildQuery(range);
    const sep = qs ? "&" : "?";
    const extra = validBuckets ? `${sep}buckets=${encodeURIComponent(validBuckets)}` : "";
    const { data } = await api.get(`/accounting/payables${qs}${extra}`);
    setData(data);
  }, [range, validBuckets]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="payables-tab">
      <DateRangeFilter value={range} onChange={setRange} testidPrefix="payables-filter" />
      {validBuckets === null && (
        <div className="text-xs text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-2.5 py-1.5 mb-2" data-testid="arap-buckets-error-payables">
          Cortes inválidos. Enteros entre 1 y 365 separados por coma.
        </div>
      )}
      <ARAPSummary
        totals={data.totals}
        kind="payable"
        bucketsInput={bucketsInput}
        onBucketsChange={setBucketsInput}
      />
      <ARAPTable kind="payable" items={data.items} onSelectDoc={(d) => { setSelected(d); setOpen(true); }} />
      <PaymentDialog open={open} onOpenChange={setOpen} type="payable" document={selected} onPaid={load} />
    </div>
  );
}

function MovementsTab() {
  const [txs, setTxs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "expense", category: "general", amount: 0, description: "", payment_method: "efectivo" });
  const [range, setRange] = useState(defaultRange30());
  const confirm = useConfirm();

  const load = useCallback(async () => {
    const qs = buildQuery(range);
    const [t, s] = await Promise.all([
      api.get(`/accounting/transactions${qs}`),
      api.get(`/accounting/summary${qs}`),
    ]);
    setTxs(t.data); setSummary(s.data);
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/accounting/transactions", { ...form, amount: Number(form.amount) });
      toast.success("Movimiento registrado"); setOpen(false); load();
      setForm({ type: "expense", category: "general", amount: 0, description: "", payment_method: "efectivo" });
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: "¿Eliminar movimiento?",
      description: "Esta acción no se puede deshacer.",
      confirmLabel: "Eliminar",
      destructive: true,
    });
    if (!ok) return;
    await api.delete(`/accounting/transactions/${id}`); load();
  };

  return (
    <>
      <DateRangeFilter value={range} onChange={setRange} testidPrefix="movements-filter" />
      {summary && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-3">
            <div className="kpi-card"><div className="overline">Ventas</div><div className="text-2xl font-black mt-2 data-cell">{formatDOP(summary.total_sales)}</div><div className="text-xs text-muted-foreground mt-1">{summary.invoices_count} facturas</div></div>
            <div className="kpi-card"><div className="overline">Compras</div><div className="text-2xl font-black mt-2 data-cell">{formatDOP(summary.total_purchases)}</div><div className="text-xs text-muted-foreground mt-1">{summary.purchases_count || 0} compras</div></div>
            <div className="kpi-card"><div className="overline">Otros ingresos</div><div className="text-2xl font-black mt-2 data-cell">{formatDOP(summary.extra_income)}</div></div>
            <div className="kpi-card"><div className="overline">Egresos</div><div className="text-2xl font-black mt-2 data-cell">{formatDOP(summary.expenses)}</div></div>
            <div className="kpi-card border-primary"><div className="overline text-primary">Neto</div><div className={`text-2xl font-black mt-2 data-cell ${summary.net >= 0 ? "text-success" : "text-destructive"}`}>{formatDOP(summary.net)}</div></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            <div className="kpi-card" data-testid="kpi-itbis-collected"><div className="overline">ITBIS recolectado (ventas)</div><div className="text-2xl font-black mt-2 data-cell text-success">{formatDOP(summary.itbis_collected || 0)}</div></div>
            <div className="kpi-card" data-testid="kpi-itbis-paid"><div className="overline">ITBIS pagado (compras)</div><div className="text-2xl font-black mt-2 data-cell text-info">{formatDOP(summary.itbis_paid || 0)}</div></div>
            <div className="kpi-card border-primary" data-testid="kpi-itbis-balance">
              <div className="overline text-primary">A pagar a DGII (saldo)</div>
              <div className={`text-2xl font-black mt-2 data-cell ${(summary.itbis_balance || 0) >= 0 ? "text-primary" : "text-success"}`}>
                {formatDOP(summary.itbis_balance || 0)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {(summary.itbis_balance || 0) >= 0 ? "Por declarar" : "Crédito a favor"}
              </div>
            </div>
          </div>
        </>
      )}
      <div className="flex justify-end mb-3">
        <Button onClick={() => setOpen(true)} className="bg-primary font-bold" data-testid="new-tx-btn"><Plus className="w-4 h-4 mr-1.5" /> Nuevo movimiento</Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuevo movimiento</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-2 gap-4">
            <div>
              <Label className="overline">Tipo</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="income">Ingreso</SelectItem>
                  <SelectItem value="expense">Egreso</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="overline">Categoría</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1" /></div>
            <div><Label className="overline">Monto (DOP)</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1 font-mono" /></div>
            <div>
              <Label className="overline">Pago</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="efectivo">Efectivo</SelectItem>
                  <SelectItem value="tarjeta">Tarjeta</SelectItem>
                  <SelectItem value="transferencia">Transferencia</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="overline">Descripción</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
            <DialogFooter className="col-span-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-primary font-bold">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <div className="bg-card border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="overline border-b border-border">
            <tr><th className="text-left p-3">Fecha</th><th className="text-left p-3">Tipo</th><th className="text-left p-3">Categoría</th><th className="text-left p-3">Descripción</th><th className="text-right p-3">Monto</th><th className="text-right p-3 w-16"></th></tr>
          </thead>
          <tbody>
            {txs.map((t) => (
              <tr key={t.id} className="border-b border-border/60 hover:bg-accent">
                <td className="p-3 text-xs text-muted-foreground">{formatDate(t.date)}</td>
                <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded ${t.type === "income" ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"}`}>{t.type === "income" ? "Ingreso" : "Egreso"}</span></td>
                <td className="p-3 text-xs">{t.category}</td>
                <td className="p-3">{t.description}</td>
                <td className={`p-3 text-right data-cell font-bold ${t.type === "income" ? "text-success" : "text-destructive"}`}>{formatDOP(t.amount)}</td>
                <td className="p-3 text-right"><Button size="icon" variant="ghost" onClick={() => remove(t.id)}><Trash2 className="w-3.5 h-3.5" /></Button></td>
              </tr>
            ))}
            {txs.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Sin movimientos en este periodo</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default function Accounting() {
  const { user } = useAuth();
  const { isEnabled } = useBrand();
  const isAccountant = ["admin", "contador"].includes((user?.role || "").toLowerCase());
  const assetsOn = isEnabled("fixed_assets");
  const bankOn = isEnabled("bank_recon");
  // Hash sync: #assets / #bank / #journal ... → permite al HelpButton saber qué tab está activo
  const initialHash = (typeof window !== "undefined" ? window.location.hash.replace(/^#/, "") : "") || (isAccountant ? "journal" : "receivables");
  const [tab, setTab] = React.useState(initialHash);
  React.useEffect(() => {
    try {
      window.history.replaceState(null, "", `#${tab}`);
      window.dispatchEvent(new HashChangeEvent("hashchange"));
    } catch (_) {}
  }, [tab]);
  return (
    <PageContainer>
      <PageHeader
        title="Contabilidad General"
        subtitle="Partida doble (DGII), cuentas por cobrar/pagar, movimientos e informes financieros."
        testid="accounting-header"
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 flex flex-wrap gap-1 h-auto">
          {isAccountant && (
            <>
              <TabsTrigger value="coa" data-testid="tab-coa">Catálogo</TabsTrigger>
              <TabsTrigger value="journal" data-testid="tab-journal">Asientos</TabsTrigger>
              <TabsTrigger value="trial-balance" data-testid="tab-trial-balance">Balance de comprobación</TabsTrigger>
              <TabsTrigger value="ledger" data-testid="tab-ledger">Libro Mayor</TabsTrigger>
              <TabsTrigger value="income" data-testid="tab-income">Estado de Resultados</TabsTrigger>
              <TabsTrigger value="balance-sheet" data-testid="tab-balance-sheet">Balance General</TabsTrigger>
              <TabsTrigger value="periods" data-testid="tab-periods">Cierre de Períodos</TabsTrigger>
              <TabsTrigger value="year-end" data-testid="tab-year-end">Cierre Anual</TabsTrigger>
            </>
          )}
          <TabsTrigger value="receivables" data-testid="tab-receivables">Cuentas por cobrar</TabsTrigger>
          <TabsTrigger value="payables" data-testid="tab-payables">Cuentas por pagar</TabsTrigger>
          <TabsTrigger value="movements" data-testid="tab-movements">Movimientos & ITBIS</TabsTrigger>
          {isAccountant && assetsOn && (
            <TabsTrigger value="assets" data-testid="tab-assets">Activos Fijos</TabsTrigger>
          )}
          {isAccountant && bankOn && (
            <TabsTrigger value="bank" data-testid="tab-bank">Conciliación Bancaria</TabsTrigger>
          )}
        </TabsList>
        {isAccountant && (
          <>
            <TabsContent value="coa"><CoaTab /></TabsContent>
            <TabsContent value="journal"><JournalTab /></TabsContent>
            <TabsContent value="trial-balance"><TrialBalanceTab /></TabsContent>
            <TabsContent value="ledger"><LedgerTab /></TabsContent>
            <TabsContent value="income"><IncomeStatementTab /></TabsContent>
            <TabsContent value="balance-sheet"><BalanceSheetTab /></TabsContent>
            <TabsContent value="periods"><PeriodCloseTab /></TabsContent>
            <TabsContent value="year-end"><YearEndTab /></TabsContent>
          </>
        )}
        <TabsContent value="receivables"><ReceivablesTab /></TabsContent>
        <TabsContent value="payables"><PayablesTab /></TabsContent>
        <TabsContent value="movements"><MovementsTab /></TabsContent>
        {isAccountant && assetsOn && (
          <TabsContent value="assets"><AssetsTab /></TabsContent>
        )}
        {isAccountant && bankOn && (
          <TabsContent value="bank"><BankTab /></TabsContent>
        )}
      </Tabs>
    </PageContainer>
  );
}
